// ---------------------------------------------------------------------------
// Cart state.
//
// The cart lives in localStorage (same key as the original design) but is only
// ever *displayed* from server-validated lines: whenever it changes, the API
// re-prices it and reports what changed, so a stale price or a sold-out size
// heals itself instead of surviving to checkout.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../lib/apiClient.js';
import { useSite } from './SiteProvider.jsx';
import { useToast } from './ToastProvider.jsx';

const CartContext = createContext(null);

const STORAGE_KEY = 'connix-cart';

const readStoredCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Stable identity for a cart line: one per product + size (or custom volume). */
const lineKey = (item) =>
  item.isCustom ? `${item.productId}:custom:${item.customMl}` : `${item.productId}:${item.variantId}`;

export function CartProvider({ children }) {
  const { t } = useSite();
  const toast = useToast();

  const [items, setItems] = useState(readStoredCart);
  const [validated, setValidated] = useState({ lines: [], subtotal: 0, itemCount: 0, adjustments: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const seenAdjustments = useRef(new Set());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Re-price whenever the cart contents change.
  useEffect(() => {
    let cancelled = false;

    if (!items.length) {
      setValidated({ lines: [], subtotal: 0, itemCount: 0, adjustments: [] });
      return undefined;
    }

    setIsValidating(true);
    api
      .post('/cart/validate', { items: items.map((item) => ({ ...item, key: lineKey(item) })) })
      .then((result) => {
        if (cancelled) return;
        setValidated(result);

        // Drop lines the server refused, so localStorage cannot drift.
        const removed = result.adjustments.filter((adjustment) => adjustment.type === 'removed');
        if (removed.length) {
          const removedKeys = new Set(removed.map((adjustment) => adjustment.key));
          setItems((current) => current.filter((item) => !removedKeys.has(lineKey(item))));
          toast.show(t('itemRemoved'), 'error');
        }

        // Tell the customer once if a price moved while the cart sat idle.
        for (const adjustment of result.adjustments) {
          if (adjustment.type !== 'repriced') continue;
          const signature = `${adjustment.key}:${adjustment.to}`;
          if (seenAdjustments.current.has(signature)) continue;
          seenAdjustments.current.add(signature);
          toast.show(t('cartUpdated'), 'info');
        }
      })
      .catch(() => {
        if (!cancelled) toast.show(t('errorNetwork'), 'error');
      })
      .finally(() => {
        if (!cancelled) setIsValidating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [items, t, toast]);

  const add = useCallback(
    (entry, { silent = false } = {}) => {
      const key = lineKey(entry);
      setItems((current) => {
        const existing = current.find((item) => lineKey(item) === key);
        if (existing) {
          return current.map((item) =>
            lineKey(item) === key
              ? { ...item, quantity: Math.min(99, item.quantity + (entry.quantity ?? 1)) }
              : item,
          );
        }
        return [...current, { ...entry, quantity: entry.quantity ?? 1 }];
      });
      if (!silent) toast.show(t('addedToCart'), 'success');
    },
    [t, toast],
  );

  const setQuantity = useCallback((key, quantity) => {
    setItems((current) =>
      quantity <= 0
        ? current.filter((item) => lineKey(item) !== key)
        : current.map((item) => (lineKey(item) === key ? { ...item, quantity: Math.min(99, quantity) } : item)),
    );
  }, []);

  const remove = useCallback((key) => {
    setItems((current) => current.filter((item) => lineKey(item) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({
      items,
      lines: validated.lines,
      subtotal: validated.subtotal,
      itemCount: validated.itemCount,
      adjustments: validated.adjustments,
      hasUnavailable: validated.lines.some((line) => !line.available),
      isEmpty: items.length === 0,
      isValidating,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      add,
      setQuantity,
      remove,
      clear,
      lineKey,
    }),
    [items, validated, isValidating, isOpen, add, setQuantity, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used inside <CartProvider>');
  return context;
}
